import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UiPreferenceValues, useBackofficeUiPreferences } from 'src/shared/backofficeUiPreferences';
import type { ElementSize } from 'src/shared/hooks/useElementSize';

export type PropertiesPanelDock = 'right' | 'side';
export type WorkspaceLayoutMode = 'workspace' | 'drawer';
export type SectionRails = 'compact' | 'expanded';
export type ColorScheme = 'system' | 'light' | 'dark';
export type PanelKey = 'left' | 'right' | 'assets';
export type MeasurementUnit = 'm' | 'cm' | 'mm' | 'in';
export type SelectionHighlightSetting = 'on' | 'off';

export interface WorkspacePreferences {
    colorScheme: ColorScheme;
    layoutMode: WorkspaceLayoutMode;
    propertiesPanel: PropertiesPanelDock;
    sectionRails: SectionRails;
    measurementUnit: MeasurementUnit;
    selectionHighlight: SelectionHighlightSetting;
    highlightColor: string;
    collapsed: PanelKey[];
    sizes: Record<PanelKey, number>;
}

// Le sei scelte di chi lavora. Il resto di `WorkspacePreferences` è geometria (`sizes`,
// `collapsed`) o non è una scelta di nessuno (`layoutMode`).
export type WorkspacePreferenceKey = 'colorScheme' | 'propertiesPanel' | 'sectionRails' | 'measurementUnit' | 'selectionHighlight' | 'highlightColor';
export type WorkspacePreferencesSnapshot = Partial<Record<WorkspacePreferenceKey, string>>;

// Le preferenze stanno sul server, nel documento di `src/shared/backofficeUiPreferences.tsx`, sotto
// questo scope: le sei scelte e le larghezze delle colonne. Contratto:
// docs/preferenze-interfaccia-microservizio.md.
const SCOPE = 'scene-editor';

// In locale resta solo `collapsed`: quali pannelli sono chiusi è uno stato del momento, come la
// sidebar. È la chiave che prima teneva tutto, ed è quindi anche quella da cui si migra.
const LOCAL_KEY = 'zakeke.sceneEditor.workspace';

// I default di fabbrica sono quelli del prototipo: le proprietà accanto alla lista, e i
// rail che scrivono i nomi. Non sono una preferenza di gusto — sono la schermata che
// chiunque apra per la prima volta deve vedere, ed è quella su cui il disegno è stato
// giudicato. L'altro attracco resta a un clic, in Settings → Interface.
const DEFAULT_PREFERENCES: WorkspacePreferences = {
    colorScheme: 'system',
    layoutMode: 'workspace',
    propertiesPanel: 'side',
    sectionRails: 'expanded',
    measurementUnit: 'm',
    selectionHighlight: 'on',
    // Il colore con cui l'editor nasce in `load3DScene`.
    highlightColor: '#FF5500',
    collapsed: [],
    // `right` non si legge da qui: la seconda colonna nasce larga come la prima — vedi `getDefaultSize`.
    sizes: { left: 332, right: 332, assets: 284 }
};

// UNA SCELTA SI CONTROLLA CONTRO IL SUO ELENCO, un colore contro la sua forma: gli esadecimali
// possibili sono sedici milioni e mezzo, e l'elenco non si scrive.
type ListPreferenceKey = Exclude<WorkspacePreferenceKey, 'highlightColor'>;

const ALLOWED_VALUES: Record<ListPreferenceKey, readonly string[]> = {
    colorScheme: ['system', 'light', 'dark'],
    propertiesPanel: ['right', 'side'],
    sectionRails: ['compact', 'expanded'],
    measurementUnit: ['m', 'cm', 'mm', 'in'],
    selectionHighlight: ['on', 'off']
};
const HEX = /^#[0-9a-fA-F]{6}$/;

const LIST_PREFERENCE_KEYS = Object.keys(ALLOWED_VALUES) as ListPreferenceKey[];
const PREFERENCE_KEYS: WorkspacePreferenceKey[] = [...LIST_PREFERENCE_KEYS, 'highlightColor'];

const PANELS: PanelKey[] = ['left', 'right', 'assets'];

// LA LARGHEZZA È DEL POSTO, NON DELLA SEZIONE: si salva «quanto è larga la prima colonna», non
// «quanto è largo l'Outliner», e le altre superfici contano i loro posti allo stesso modo. La
// striscia in basso è un'altezza, e per questo ha un nome suo.
const SIZE_KEYS: Record<PanelKey, string> = { left: 'panel1', right: 'panel2', assets: 'strip' };

// I DUE NUMERI PARLANO DEL CONTENUTO, non della colonna: trecentocinquanta è dove una lista smette
// di essere leggibile, cinquecento è dove smette di guadagnarci. IL RAIL NON È CONTENUTO — è il
// bordo da cui si entra — quindi la colonna che se lo porta dietro lo aggiunge a tutt'e due i capi,
// e una colonna col rail e una senza mostrano la stessa quantità di roba.
const MIN_CONTENT = 350;
const MAX_CONTENT = 500;

// Oltre il 2K lo spazio smette di essere il vincolo: cento pixel in più a chi legge una lista lunga
// non li toglie a nessuno. Sotto, restano cinquecento.
const WIDE_WORKSPACE = 2048;
const MAX_CONTENT_WIDE = 600;

const RAIL_WIDTHS: Record<SectionRails, number> = { compact: 43, expanded: 70 };

// La striscia sotto il palco è un'altezza e rail non ne ha: il suo minimo è la sua barra chiusa.
const STRIP_MIN = 43;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// CHI PORTA IL RAIL: la prima colonna sempre, l'Inspector solo nell'attracco in cui ne ha uno suo,
// la striscia mai. Non è un dettaglio grafico — decide i limiti, e nell'altro attracco l'Inspector
// senza rail deve poter arrivare agli stessi trecentocinquanta di contenuto.
const getRailWidth = (panel: PanelKey, sectionRails: SectionRails, dock: PropertiesPanelDock) =>
    panel === 'left' || (panel === 'right' && dock === 'right') ? RAIL_WIDTHS[sectionRails] : 0;

const getPanelMinimum = (panel: PanelKey, sectionRails: SectionRails, dock: PropertiesPanelDock) =>
    panel === 'assets' ? STRIP_MIN : MIN_CONTENT + getRailWidth(panel, sectionRails, dock);

const getPanelMaximum = (panel: PanelKey, sectionRails: SectionRails, dock: PropertiesPanelDock, workspace: ElementSize | null) =>
    ((workspace?.width ?? 0) > WIDE_WORKSPACE ? MAX_CONTENT_WIDE : MAX_CONTENT) + getRailWidth(panel, sectionRails, dock);

// Il default della superficie, riportato dentro i limiti: il minimo di una colonna supera il suo
// default, e il default vero diventa quel minimo.
const getPanelDefault = (panel: PanelKey, sectionRails: SectionRails, dock: PropertiesPanelDock, workspace: ElementSize | null) =>
    clamp(DEFAULT_PREFERENCES.sizes[panel], getPanelMinimum(panel, sectionRails, dock), getPanelMaximum(panel, sectionRails, dock, workspace));

// LE DUE COLONNE NASCONO LARGHE UGUALI (utente, 2026-09-22), e la misura è quella della PRIMA —
// che è anche la colonna del Composer, perché quell'ambiente usa questo stesso hook e ha solo
// quella. Due ambienti della stessa piattaforma che si aprono con le colonne di misura diversa
// sembrano due prodotti diversi.
//
// Non è un numero scritto qui: la prima colonna si porta dentro il rail, e quanto è larga cambia
// col rail stesso — stretto o coi nomi. Per questo la si CHIEDE invece di ricopiarne il risultato,
// che al primo rail diverso tornerebbe a mentire. Chi la trascina poi la mette dove vuole: questo
// è solo il punto di partenza.
const getDefaultSize = (panel: PanelKey, sectionRails: SectionRails, dock: PropertiesPanelDock, workspace: ElementSize | null) => {
    if (panel !== 'right') return getPanelDefault(panel, sectionRails, dock, workspace);
    return clamp(
        getPanelDefault('left', sectionRails, dock, workspace),
        getPanelMinimum('right', sectionRails, dock),
        getPanelMaximum('right', sectionRails, dock, workspace)
    );
};

const getExtent = (panel: PanelKey, workspace: ElementSize | null) => (panel === 'assets' ? workspace?.height : workspace?.width) ?? 0;

// IL NUMERO SALVATO È UNA FRAZIONE DEL WORKSPACE, non un conto di pixel: un px misurato su un 27"
// su un portatile non vuol dire niente, una frazione vuol dire ancora quello che voleva dire.
// Quattro decimali: su 2000 px l'errore di riconversione sta sotto il mezzo pixel.
const toFraction = (panel: PanelKey, size: number, workspace: ElementSize | null) => {
    const extent = getExtent(panel, workspace);
    return extent > 0 ? (size / extent).toFixed(4) : undefined;
};

// E SI RIVALIDA A OGNI LETTURA, non si applica: i limiti dipendono dai rail, dall'attracco e dalla
// larghezza dello schermo, e un valore salvato altrove può essere fuori scala qui.
const resolveSize = (panel: PanelKey, stored: string | undefined, workspace: ElementSize | null, sectionRails: SectionRails, dock: PropertiesPanelDock) => {
    const fraction = Number(stored);
    const extent = getExtent(panel, workspace);
    if (stored === undefined || extent === 0 || !(fraction > 0 && fraction < 1)) return getDefaultSize(panel, sectionRails, dock, workspace);
    return clamp(fraction * extent, getPanelMinimum(panel, sectionRails, dock), getPanelMaximum(panel, sectionRails, dock, workspace));
};

// Il confronto è con TUTTI i valori ammessi, non solo con quello diverso dal default:
// scritto all'incontrario, cambiare il default riscriverebbe la scelta di chi aveva
// preso l'altra.
const pick = <K extends ListPreferenceKey>(key: K, value: string | undefined): WorkspacePreferences[K] =>
    (value !== undefined && ALLOWED_VALUES[key].includes(value) ? value : DEFAULT_PREFERENCES[key]) as WorkspacePreferences[K];

const pickColor = (value: string | undefined) => (value !== undefined && HEX.test(value) ? value : DEFAULT_PREFERENCES.highlightColor);

const resolvePreferences = (stored: UiPreferenceValues, collapsed: PanelKey[], workspace: ElementSize | null): WorkspacePreferences => {
    const sectionRails = pick('sectionRails', stored.sectionRails);
    // L'attracco si risolve PRIMA delle misure, perché dice quale colonna porta il rail e quindi
    // quanto è larga al minimo.
    const propertiesPanel = pick('propertiesPanel', stored.propertiesPanel);

    return {
        colorScheme: pick('colorScheme', stored.colorScheme),
        // L'Inspector a cassetto non è una scelta offerta: lo step 5 lo vuole colonna, e nel
        // prototipo il cassetto non esiste. Per questo non si salva nemmeno.
        layoutMode: 'workspace',
        propertiesPanel,
        sectionRails,
        measurementUnit: pick('measurementUnit', stored.measurementUnit),
        selectionHighlight: pick('selectionHighlight', stored.selectionHighlight),
        highlightColor: pickColor(stored.highlightColor),
        collapsed,
        sizes: {
            left: resolveSize('left', stored[SIZE_KEYS.left], workspace, sectionRails, propertiesPanel),
            right: resolveSize('right', stored[SIZE_KEYS.right], workspace, sectionRails, propertiesPanel),
            assets: resolveSize('assets', stored[SIZE_KEYS.assets], workspace, sectionRails, propertiesPanel)
        }
    };
};

// Ciò che sta nella chiave locale. Finché la migrazione non è avvenuta ci sono ancora le voci di
// quando la chiave teneva tutto — le larghezze in pixel.
type LocalPreferences = Partial<Omit<WorkspacePreferences, 'sizes'>> & { sizes?: Partial<Record<PanelKey, number>> };

const readLocal = (): LocalPreferences => {
    if (typeof window === 'undefined') return {};

    try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(LOCAL_KEY) ?? '{}');
        return typeof stored === 'object' && stored !== null ? (stored as LocalPreferences) : {};
    } catch {
        return {};
    }
};

const writeLocal = (value: LocalPreferences) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(LOCAL_KEY, JSON.stringify(value));
};

const readCollapsed = (local: LocalPreferences): PanelKey[] =>
    Array.isArray(local.collapsed) ? local.collapsed.filter((panel): panel is PanelKey => PANELS.includes(panel)) : [];

const readLegacyPreferences = (local: LocalPreferences) => {
    const entries: WorkspacePreferencesSnapshot = {};
    for (const key of LIST_PREFERENCE_KEYS) {
        const value = local[key];
        if (typeof value === 'string' && ALLOWED_VALUES[key].includes(value)) entries[key] = value;
    }
    return entries;
};

// L'ALONE DELLA SELEZIONE AVEVA UNA CHIAVE SUA (2026-09-15), perché il servizio non lo teneva
// ancora. Ora lo tiene, e quella chiave serve un'ultima volta: si porta su e si butta.
const HIGHLIGHT_LOCAL_KEY = 'zakeke.sceneEditor.selectionHighlight';

const readLegacyHighlight = (): WorkspacePreferencesSnapshot => {
    if (typeof window === 'undefined') return {};

    try {
        const stored = JSON.parse(window.localStorage.getItem(HIGHLIGHT_LOCAL_KEY) ?? 'null') as { enabled?: unknown; color?: unknown } | null;
        if (!stored || typeof stored !== 'object') return {};

        const entries: WorkspacePreferencesSnapshot = {};
        if (typeof stored.enabled === 'boolean') entries.selectionHighlight = stored.enabled ? 'on' : 'off';
        if (typeof stored.color === 'string' && HEX.test(stored.color)) entries.highlightColor = stored.color;
        return entries;
    } catch {
        return {};
    }
};

const dropLegacyHighlight = () => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(HIGHLIGHT_LOCAL_KEY);
};

export const useWorkspacePreferences = (workspace: ElementSize | null) => {
    const { values: stored, isStored, save, fill } = useBackofficeUiPreferences(SCOPE);
    const [collapsed, setCollapsed] = useState(() => readCollapsed(readLocal()));
    const preferences = useMemo(() => resolvePreferences(stored, collapsed, workspace), [stored, collapsed, workspace]);

    // LA MIGRAZIONE: ciò che questo browser si ricordava prima che le preferenze avessero un posto sul
    // server. Riempie i buchi e non corregge niente — se il server una scelta ce l'ha già, fatta qui o
    // su un altro computer, vince lui. La copia locale si butta solo quando il server ha confermato di
    // avere questa superficie: prima, una scrittura fallita se la porterebbe via.
    //
    // Prima del disegno, perché chi aveva scelto il tema scuro non veda un lampo chiaro. Le larghezze
    // erano pixel e diventano frazioni, quindi aspettano la prima misura del workspace.
    //
    // Con loro si tengono i RAIL DI ALLORA: «era il default?» va chiesto ai rail con cui quei pixel
    // sono stati salvati. Coi rail di adesso, un 332 salvato coi rail compatti — il loro default —
    // sembrerebbe una larghezza scelta, se la misura arriva prima che i rail portati su siano a schermo.
    // `undefined`: la chiave locale non è ancora stata letta; `null`: non c'è niente da portare.
    const legacySizes = useRef<{ sizes: NonNullable<LocalPreferences['sizes']>; sectionRails: SectionRails } | null | undefined>(undefined);

    useLayoutEffect(() => {
        const local = readLocal();
        const entries = { ...readLegacyPreferences(local), ...readLegacyHighlight() };
        if (Object.keys(entries).length === 0 && !local.sizes) return;
        if (legacySizes.current === undefined) {
            legacySizes.current = local.sizes ? { sizes: local.sizes, sectionRails: pick('sectionRails', entries.sectionRails) } : null;
            fill(entries);
        }
        if (isStored) {
            writeLocal({ collapsed: readCollapsed(local) });
            dropLegacyHighlight();
        }
    }, [fill, isStored]);

    useLayoutEffect(() => {
        const legacy = legacySizes.current;
        if (!legacy || !workspace) return;
        legacySizes.current = null;

        // I pixel di allora si riportano dentro i limiti di ADESSO: quelli di allora non sono
        // scritti da nessuna parte, e una misura fuori scala si vedrebbe comunque riportata dentro
        // alla prima lettura.
        const dock = preferences.propertiesPanel;
        const entries: Record<string, string> = {};
        for (const panel of PANELS) {
            const size = Number(legacy.sizes[panel]);
            if (!size || size === getDefaultSize(panel, legacy.sectionRails, dock, workspace)) continue;
            const inRange = clamp(size, getPanelMinimum(panel, legacy.sectionRails, dock), getPanelMaximum(panel, legacy.sectionRails, dock, workspace));
            const fraction = toFraction(panel, inRange, workspace);
            if (fraction) entries[SIZE_KEYS[panel]] = fraction;
        }
        fill(entries);
    }, [fill, preferences.propertiesPanel, workspace]);

    const setPreference = useCallback(<K extends WorkspacePreferenceKey>(key: K, value: WorkspacePreferences[K]) => save({ [key]: value }), [save]);

    // Si scrive al rilascio della presa, non a ogni movimento. E chi non è stato trascinato non si
    // scrive: tornare al default — il doppio clic sulla presa — toglie la voce, e il posto torna a
    // valere il default della superficie.
    const setPanelSize = useCallback(
        (panel: PanelKey, size: number) => {
            const isDefault = size === getDefaultSize(panel, preferences.sectionRails, preferences.propertiesPanel, workspace);
            const fraction = isDefault ? null : toFraction(panel, size, workspace);
            if (fraction !== undefined) save({ [SIZE_KEYS[panel]]: fraction });
        },
        [preferences.propertiesPanel, preferences.sectionRails, save, workspace]
    );

    // RIMETTERE LE MISURE COM'ERANO È TOGLIERLE, non riscriverle col numero del default: una misura
    // scritta per esteso sembrerebbe una scelta, e il giorno che il default cambia resterebbe
    // indietro. È il gesto del doppio clic su una presa, per tutt'e tre i posti insieme.
    const resetPanelSizes = useCallback(() => save(Object.fromEntries(PANELS.map((panel) => [SIZE_KEYS[panel], null]))), [save]);

    // I LIMITI SONO DI QUESTA SCHERMATA, non di una costante: dipendono dai rail, dall'attracco e da
    // quanto è largo lo schermo, e qui ci sono tutti e tre. Chi disegna le prese li riceve già fatti.
    const limits = useMemo(
        () =>
            Object.fromEntries(
                PANELS.map((panel) => [
                    panel,
                    {
                        min: getPanelMinimum(panel, preferences.sectionRails, preferences.propertiesPanel),
                        max: getPanelMaximum(panel, preferences.sectionRails, preferences.propertiesPanel, workspace)
                    }
                ])
            ) as Record<PanelKey, { min: number; max: number }>,
        [preferences.propertiesPanel, preferences.sectionRails, workspace]
    );

    // E LA MISURA DI PARTENZA ALLO STESSO MODO: è quella a cui torna il doppio clic sulla presa, e
    // scritta a mano accanto alla presa era un numero che il giorno del cambio restava indietro —
    // una colonna che nasceva di una misura e, riportata «com'era», ne prendeva un'altra.
    const defaults = useMemo(
        () =>
            Object.fromEntries(
                PANELS.map((panel) => [panel, getDefaultSize(panel, preferences.sectionRails, preferences.propertiesPanel, workspace)])
            ) as Record<PanelKey, number>,
        [preferences.propertiesPanel, preferences.sectionRails, workspace]
    );

    const toggleCollapsed = useCallback((panel: PanelKey) => {
        setCollapsed((current) => {
            const next = current.includes(panel) ? current.filter((currentPanel) => currentPanel !== panel) : [...current, panel];
            // Le voci di prima della migrazione restano dove sono: chiudere un pannello non deve
            // portarsi via le scelte che il server non ha ancora confermato.
            writeLocal({ ...readLocal(), collapsed: next });
            return next;
        });
    }, []);

    // «Cancel» nelle Settings rimette le scelte com'erano, anche la loro assenza: una voce che non
    // c'era torna a non esserci, invece di diventare il default scritto per esteso.
    const snapshotPreferences = useCallback(
        (): WorkspacePreferencesSnapshot => Object.fromEntries(PREFERENCE_KEYS.filter((key) => stored[key] !== undefined).map((key) => [key, stored[key]])),
        [stored]
    );

    const restorePreferences = useCallback(
        (snapshot: WorkspacePreferencesSnapshot) => save(Object.fromEntries(PREFERENCE_KEYS.map((key) => [key, snapshot[key] ?? null]))),
        [save]
    );

    return useMemo(
        () => ({ preferences, limits, defaults, setPreference, setPanelSize, resetPanelSizes, toggleCollapsed, snapshotPreferences, restorePreferences }),
        [defaults, limits, preferences, resetPanelSizes, restorePreferences, setPanelSize, setPreference, snapshotPreferences, toggleCollapsed]
    );
};

export const getWorkspaceRailWidth = (sectionRails: SectionRails) => RAIL_WIDTHS[sectionRails];

// Il Composer tiene le sue colonne agli stessi due numeri: le legge da qui, così restano una
// definizione sola per tutte e due le stanze.
export const WORKSPACE_PANEL_CONTENT_MIN = MIN_CONTENT;
export const WORKSPACE_PANEL_CONTENT_MAX = MAX_CONTENT;
